// SEQ-192: SEQ report widget ("Report a bug / Request a feature") for the Claims App.
//
// The pure parts live here so they can be tested under Deno without a browser:
// the script tag attributes, the module routing, the context payload and the
// idempotent loader. The React glue is components/report/SeqReportWidget.tsx.
//
// The app key below is the PUBLIC, origin-bound key for app id "claims" (the
// same kind of key the Operations Portal ships in its page source). It
// identifies the app to the report service; the origin allowlist and the
// signed-in user's own Supabase access token are the real gates.

export const SEQ_REPORT_APP = "claims";
export const SEQ_REPORT_SRC = "https://seq-feedback.vercel.app/widget.js";
export const SEQ_REPORT_ENDPOINT = "https://seq-feedback.vercel.app/api";
export const SEQ_REPORT_KEY = "pk_2a15ab018e0cfe757940926f";

// Top right, on purpose. Bottom right holds the Claims Bot button and the
// toast stack, and bottom left sits on the sidebar's Safe Mode and Sign Out
// buttons. On phones the menu button is top left, so top right stays clear.
export const SEQ_REPORT_POSITION = "top-right";

// Marker so the loader can tell its own script tag from any other.
export const SEQ_REPORT_SCRIPT_MARK = "data-seq-report-script";

export interface SeqReportUser {
  id: string;
  name: string | null;
  email: string;
  role: string;
}

export interface SeqReportContextPayload {
  user?: SeqReportUser;
  token?: string;
  module?: string;
}

interface SeqReportApi {
  setContext: (input: SeqReportContextPayload) => void;
  // Not in every widget build: feature-detected before use.
  reset?: () => void;
  open?: (...args: unknown[]) => unknown;
}

// The slice of `window` this module touches, so tests can pass a fake.
export interface SeqReportWindow {
  SEQReport?: SeqReportApi;
  // The widget's pre-load queue: a pushed { action: "open" } is honoured once,
  // right after the widget mounts, then the queue is deleted.
  SEQReportQueue?: unknown[];
  addEventListener: (type: string, listener: () => void, options?: { once?: boolean }) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

// The slice of `document` the loader touches.
export interface SeqReportDocument {
  querySelector: (selector: string) => unknown;
  createElement: (tag: string) => {
    src: string;
    async: boolean;
    onerror?: (() => void) | null;
    remove?: () => void;
    setAttribute: (name: string, value: string) => void;
  };
  body: { appendChild(node: unknown): unknown };
}

// Area of the app a route belongs to, used to route a report to the right
// Jira epic. P&L pages must come back as "pnl" so they reach SEQ-180.
// Path only: callers pass location.pathname, never the query string.
export function moduleForPath(pathname: string): string {
  const path = pathname.split("?")[0].split("#")[0];
  const segments = path.split("/").filter(Boolean);
  const first = segments[0] ?? "";
  if (first === "") return "dashboard";
  if (first === "pnl-analytics") return "pnl";
  if (first === "projects") {
    if (segments[1] === "new") return "projects";
    return segments.length > 1 ? "project" : "projects";
  }
  if (first === "super-admin") return "platform-admin";
  return first;
}

// Module override set by a screen that lives inside a route (the project P&L tab).
let moduleOverride: string | null = null;
const overrideListeners = new Set<() => void>();

export function setSeqReportModuleOverride(module: string | null): void {
  if (moduleOverride === module) return;
  moduleOverride = module;
  overrideListeners.forEach((listener) => listener());
}

export function getSeqReportModuleOverride(): string | null {
  return moduleOverride;
}

export function subscribeSeqReportModuleOverride(listener: () => void): () => void {
  overrideListeners.add(listener);
  return () => {
    overrideListeners.delete(listener);
  };
}

export function resolveSeqReportModule(pathname: string, override: string | null): string {
  return override ?? moduleForPath(pathname);
}

// Build id shown on every report: the deploy commit when the host provides
// one, then the version the Sentry release already uses, then the tour version.
export function resolveBuildId(env: {
  commit?: string;
  version?: string;
  fallback: string;
}): string {
  return env.commit || env.version || env.fallback;
}

// Taps waiting on a load that has failed (or is about to be given up on).
const failureListeners = new Set<() => void>();

// The script tag the loader added, and how to add it again. A failed or timed
// out load takes its tag out of the page, so the next tap loads it afresh.
let currentScript: { remove?: () => void } | null = null;
let reload: (() => void) | null = null;
// One queued open at a time: a fast double tap must not open the drawer twice.
let pendingOpen = false;

function dropSeqReportScript(): void {
  try {
    currentScript?.remove?.();
  } catch {
    // Detached already: nothing to remove.
  }
  currentScript = null;
}

function markSeqReportFailed(): void {
  dropSeqReportScript();
  for (const fn of Array.from(failureListeners)) fn();
}

// Test seam: forget module state between tests.
export function resetSeqReportOpenState(): void {
  failureListeners.clear();
  currentScript = null;
  reload = null;
  pendingOpen = false;
}

// Adds the widget's script tag once. Safe to call on every render: a second
// call finds the first tag and does nothing. Returns true when it added one.
export function ensureSeqReportScript(doc: SeqReportDocument, build: string): boolean {
  reload = () => {
    ensureSeqReportScript(doc, build);
  };
  if (doc.querySelector(`script[${SEQ_REPORT_SCRIPT_MARK}]`)) return false;
  const script = doc.createElement("script");
  script.src = SEQ_REPORT_SRC;
  script.async = true;
  script.onerror = markSeqReportFailed;
  script.setAttribute(SEQ_REPORT_SCRIPT_MARK, "true");
  script.setAttribute("data-app", SEQ_REPORT_APP);
  script.setAttribute("data-key", SEQ_REPORT_KEY);
  script.setAttribute("data-endpoint", SEQ_REPORT_ENDPOINT);
  script.setAttribute("data-position", SEQ_REPORT_POSITION);
  // The widget never floats over the Claims UI: the sidebar's "Report an
  // issue" item is the only way in, at every width.
  script.setAttribute("data-launcher", "never");
  script.setAttribute("data-build", build);
  doc.body.appendChild(script);
  currentScript = script;
  return true;
}

export const SEQ_REPORT_LOAD_TIMEOUT_MS = 10_000;

export type SeqReportOpenResult = "opened" | "queued" | "unavailable";

// Opens the report drawer now if the widget is ready. Never throws.
function openSeqReportNow(win: Pick<SeqReportWindow, "SEQReport">): boolean {
  try {
    const api = win.SEQReport;
    if (!api || typeof api.open !== "function") return false;
    void Promise.resolve(api.open()).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

// The "Report an issue" menu item's way in, for the phone user who has no
// floating launcher. Widget ready: open now. Widget present without open() (an
// older build): onUnavailable at once. Widget still loading: queue the open on
// window.SEQReportQueue so it happens the moment the widget mounts, and give up
// (calling onUnavailable) if the script errors or the timeout passes, taking
// the queued open back out so it cannot pop up long after the user moved on and
// taking the failed script tag out so the next tap loads it again. A second tap
// while an open is already queued adds nothing. Never throws.
export function requestSeqReportOpen(
  win: SeqReportWindow,
  onUnavailable: () => void,
  timeoutMs: number = SEQ_REPORT_LOAD_TIMEOUT_MS,
): SeqReportOpenResult {
  try {
    if (openSeqReportNow(win)) return "opened";
    if (win.SEQReport) {
      onUnavailable();
      return "unavailable";
    }
    if (pendingOpen) return "queued";
    // A tap after a failed load finds no script tag and adds a new one. With a
    // load already under way this does nothing.
    reload?.();
    const entry = { action: "open" };
    const queue = win.SEQReportQueue ?? [];
    win.SEQReportQueue = queue;
    queue.push(entry);
    pendingOpen = true;

    const timer: ReturnType<typeof setTimeout> = setTimeout(timedOut, timeoutMs);
    const settle = () => {
      clearTimeout(timer);
      pendingOpen = false;
      failureListeners.delete(fail);
      win.removeEventListener("seqreport:ready", settle);
    };
    function fail() {
      settle();
      const current = win.SEQReportQueue;
      const at = current ? current.indexOf(entry) : -1;
      if (current && at >= 0) current.splice(at, 1);
      onUnavailable();
    }
    function timedOut() {
      dropSeqReportScript();
      fail();
    }
    failureListeners.add(fail);
    win.addEventListener("seqreport:ready", settle, { once: true });
    return "queued";
  } catch {
    onUnavailable();
    return "unavailable";
  }
}

// Pushes context to the widget, waiting for its one-time seqreport:ready
// event when the script has not finished loading. Never throws: the widget is
// third party and must not be able to break Claims. Returns a cancel function.
export function pushSeqReportContext(
  win: SeqReportWindow,
  payload: SeqReportContextPayload,
): () => void {
  const send = () => {
    try {
      win.SEQReport?.setContext(payload);
    } catch {
      // A failure inside the widget stays inside the widget.
    }
  };
  if (win.SEQReport) {
    send();
    return () => {};
  }
  win.addEventListener("seqreport:ready", send, { once: true });
  return () => win.removeEventListener("seqreport:ready", send);
}

// Builds the user block the same way the Portal does: id, name, email, role.
export function buildReportUser(input: {
  id: string;
  email: string | null | undefined;
  fullName: string | null | undefined;
  role: string | null | undefined;
}): SeqReportUser {
  return {
    id: input.id,
    name: input.fullName ?? null,
    email: input.email ?? "",
    role: input.role ?? "member",
  };
}

// The empty identity pushed on sign out, so a later user on the same tab
// cannot file a report as the previous one.
export const SEQ_REPORT_SIGNED_OUT_CONTEXT: SeqReportContextPayload = {
  token: "",
  user: { id: "", name: null, email: "", role: "" },
};

// The slice of the host element the session shows and hides.
interface SeqReportHostElement {
  style: { display: string };
}

// Loads the widget and keeps it running for a signed-in session. Returns the
// teardown for sign out: it stops the money sweep, hides the button, drops the
// token and identity, and calls window.SEQReport.reset() when the widget has
// one. It never throws: the widget is third party and must not break sign out.
export function startSeqReportSession(deps: {
  win: SeqReportWindow;
  doc: SeqReportDocument;
  build: string;
  startSweep: () => () => void;
}): () => void {
  const { win, doc, build, startSweep } = deps;
  ensureSeqReportScript(doc, build);
  const host = doc.querySelector("seq-report") as SeqReportHostElement | null;
  if (host) host.style.display = "";
  let stopSweep: () => void = () => {};
  try {
    stopSweep = startSweep();
  } catch {
    // A sweep that cannot start must not stop the session from tearing down.
  }

  return () => {
    try {
      stopSweep();
    } catch {
      // Keep going: the token must still be dropped.
    }
    const el = doc.querySelector("seq-report") as SeqReportHostElement | null;
    if (el) el.style.display = "none";
    try {
      win.SEQReport?.setContext(SEQ_REPORT_SIGNED_OUT_CONTEXT);
    } catch {
      // A failure inside the widget stays inside the widget.
    }
    try {
      if (typeof win.SEQReport?.reset === "function") win.SEQReport.reset();
    } catch {
      // Same: reset is best effort.
    }
  };
}
